import React, { useEffect, useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';
import { compressImage } from 'lib/compressImage';

// Selector de fotos con vista previa.
// - Controlado: `value` (Blob[]) + `onChange(blobs)`. Así el asistente puede
//   desmontar el paso y, al volver, las fotos siguen ahí.
// - Sin `value` guarda las fotos por su cuenta (uso simple).
const mismaLista = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

export default function PhotoUpload({ value, onChange }) {
  const [interno, setInterno] = useState([]);
  const recibido = value ?? interno;

  // Referencia estable: si el padre pasa un arreglo nuevo con las mismas fotos
  // (por ejemplo `detalle?.fotoBlobs || []` en cada render), se reusa el
  // anterior y las vistas previas no se recrean.
  const estableRef = useRef(recibido);
  if (!mismaLista(estableRef.current, recibido)) estableRef.current = recibido;
  const blobs = estableRef.current;

  // Lista vigente para las tandas asíncronas: comprimir tarda, y una tanda que
  // termina después de otra debe sumarse a lo que ya hay, no a lo que había
  // cuando empezó.
  const vigenteRef = useRef(blobs);
  vigenteRef.current = blobs;

  // Las URLs de vista previa se crean y se revocan en el MISMO efecto (con
  // useMemo, el doble montaje de StrictMode revocaba URLs todavía en uso).
  const [urls, setUrls] = useState([]);
  useEffect(() => {
    const puede = typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';
    const nuevas = blobs.map((b) => (puede ? URL.createObjectURL(b) : ''));
    setUrls(nuevas);
    return () => nuevas.forEach((u) => u && URL.revokeObjectURL?.(u));
  }, [blobs]);

  const emitir = (next) => {
    vigenteRef.current = next;
    if (value === undefined) setInterno(next);
    onChange?.(next);
  };

  const handleFiles = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    // Permite volver a escoger la misma foto después de quitarla.
    input.value = '';
    const nuevos = [];
    for (const f of files) nuevos.push(await compressImage(f));
    if (nuevos.length) emitir([...vigenteRef.current, ...nuevos]);
  };
  const remove = (i) => emitir(vigenteRef.current.filter((_, idx) => idx !== i));

  return (
    <div>
      <label className="inline-flex min-h-[48px] cursor-pointer items-center gap-2 rounded-xl border border-input bg-white px-4 text-base font-semibold text-foreground shadow-card transition-colors hover:border-primary hover:bg-primary/5 active:scale-[0.98]">
        <Camera size={20} className="text-primary" strokeWidth={2.25} aria-hidden="true" />
        {blobs.length > 0 ? 'Agregar otra foto' : 'Agregar foto'}
        {blobs.length > 0 && (
          <span className="ml-1 inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold text-white tabular-nums">
            {blobs.length}
          </span>
        )}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={handleFiles}
        />
      </label>
      {blobs.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {blobs.map((blob, i) => (
            <div
              key={urls[i] || i}
              className="relative overflow-hidden rounded-xl border border-border shadow-card"
            >
              <img src={urls[i] || undefined} alt={`Foto ${i + 1}`} className="h-24 w-full object-cover" />
              {/* Área táctil de 48 px; el círculo visible es más chico. */}
              <button
                type="button"
                onClick={() => remove(i)}
                aria-label={`Quitar foto ${i + 1}`}
                className="absolute right-0 top-0 flex h-12 w-12 items-start justify-end p-1"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white">
                  <X size={15} strokeWidth={2.75} aria-hidden="true" />
                </span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
